import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
  AddressElement,
  ContactDetailsElement,
} from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions, StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { createCheckout, validateCoupon } from '../services/api';
import './CheckoutPage.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

// Austin, TX zip codes
const AUSTIN_ZIP_CODES = [
  '78701','78702','78703','78704','78705','78721','78722','78723','78724',
  '78741','78742','78745','78746','78747','78748','78749','78750','78751',
  '78752','78753','78754','78756','78757','78758','78759','78760','78761',
  '78762','78763','78764','78765','78766','78767','78768','78769','78772',
  '78773','78774','78775','78776','78777','78778','78779','78780','78781',
  '78782','78783','78784','78785','78786','78787','78788','78789','78798','78799',
];

function isAustinZipCode(zipCode: string): boolean {
  return AUSTIN_ZIP_CODES.includes(zipCode.trim());
}

/**
 * Elements options using mode-based configuration.
 * This allows both PaymentElement and AddressElement to render immediately
 * without needing a clientSecret upfront.
 */
const elementsOptions: StripeElementsOptions = {
  mode: 'payment',
  amount: 100, // placeholder — actual amount is determined at confirm time
  currency: 'usd',
  appearance: { theme: 'stripe' },
};

/**
 * Inner checkout form that has access to Stripe Elements context.
 * Renders AddressElement + PaymentElement immediately on mount.
 */
const CheckoutForm: React.FC<{ onPaymentFailed: () => void }> = ({ onPaymentFailed }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { items, subtotalDisplay, subtotalCents, clearCart } = useCart();

  const [customerEmail, setCustomerEmail] = useState('');
  const [shippingAddress, setShippingAddress] = useState<any>(null);
  const [selectedShipping, setSelectedShipping] = useState('standard');
  const [shippingCost, setShippingCost] = useState(499);
  const [isAustin, setIsAustin] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState<'idle' | 'valid' | 'invalid' | 'checking'>('idle');
  const [couponMessage, setCouponMessage] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [overrideTotal, setOverrideTotal] = useState<number | null>(null);

  // Handle address changes — detect Austin zip for free shipping
  const handleAddressChange = useCallback((event: StripeAddressElementChangeEvent) => {
    if (event.complete) {
      const address = event.value?.address;
      setShippingAddress({
        line1: address?.line1 || '',
        line2: address?.line2 || undefined,
        city: address?.city || '',
        state: address?.state || '',
        postal_code: address?.postal_code || '',
        country: address?.country || 'US',
      });

      const postalCode = address?.postal_code || '';
      if (isAustinZipCode(postalCode)) {
        setIsAustin(true);
        setSelectedShipping('free');
        setShippingCost(0);
      } else {
        setIsAustin(false);
        setSelectedShipping('standard');
        setShippingCost(499);
      }
    } else {
      setShippingAddress(null);
    }
  }, []);

  // Handle coupon validation
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponStatus('checking');
    try {
      const result = await validateCoupon(couponCode.trim());
      if (result.valid) {
        setCouponStatus('valid');
        setCouponMessage(result.description || 'Coupon applied!');
        if (result.freeShipping) {
          setShippingCost(0);
        }
        if (result.percentOff) {
          setDiscountPercent(result.percentOff);
        }
        if (result.overrideTotalCents !== null && result.overrideTotalCents !== undefined) {
          setOverrideTotal(result.overrideTotalCents);
        }
      } else {
        setCouponStatus('invalid');
        setCouponMessage(result.message || 'Invalid coupon code');
      }
    } catch {
      setCouponStatus('invalid');
      setCouponMessage('Unable to validate coupon. Try again.');
    }
  };

  // Handle purchase: validate elements, call backend, confirm payment
  const handlePurchase = async () => {
    if (!stripe || !elements) {
      setError('Payment system not ready. Please refresh the page.');
      return;
    }

    if (!shippingAddress) {
      setError('Please complete your shipping address.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Always validate/submit elements first — required before every confirmPayment().
      const submitResult = await elements.submit();
      if (submitResult.error) {
        setError(submitResult.error.message || 'Please check your information and try again.');
        setIsProcessing(false);
        return;
      }

      // Always create a FRESH PaymentIntent per attempt. Per Stripe guidance, a
      // PaymentIntent that already failed should not be reused — doing so can trigger
      // fraud-prevention blocks. A new intent each attempt mirrors what re-entering
      // the card does manually.
      const response = await createCheckout({
        items: items.map((item) => ({
          productId: item.productId || item.id,
          variantValue: item.variantValue || item.variantLabel || item.id,
          quantity: item.quantity,
        })),
        shippingAddress,
        email: customerEmail,
        couponCode: couponCode.trim() || undefined,
      });

      // Handle free orders (coupon covers full amount — no Stripe needed)
      if (response.free) {
        clearCart();
        navigate('/order-confirmation');
        return;
      }

      if (!response.clientSecret) {
        setError('Failed to initialize payment. Please try again.');
        setIsProcessing(false);
        return;
      }

      // 3. Confirm payment with the fresh clientSecret
      const confirmResult = await stripe.confirmPayment({
        elements,
        clientSecret: response.clientSecret as string,
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation`,
          receipt_email: customerEmail,
        },
        redirect: 'if_required',
      });

      if (confirmResult.error) {
        // Diagnostic: log the full error so we can see type/code/decline_code on retry
        if (
          confirmResult.error.type === 'api_error' ||
          confirmResult.error.type === 'api_connection_error'
        ) {
          setError('Connection error. Please check your internet and try again.');
        } else {
          setError(confirmResult.error.message || 'Payment could not be processed. Please try again.');
        }
        setIsProcessing(false);
        // Remount Elements so the next attempt starts with a clean payment state.
        // This mirrors re-entering the card and avoids the stuck-retry issue.
        onPaymentFailed();
      } else {
        // Payment successful
        clearCart();
        navigate('/order-confirmation');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      setIsProcessing(false);
    }
  };

  if (items.length === 0) {
    navigate('/');
    return null;
  }

  return (
    <div className="checkout-page">
      <div className="checkout-page__container">
        {/* Breadcrumb */}
        <div className="checkout-page__breadcrumb">
          <span>Cart</span>
          <span className="checkout-page__breadcrumb-sep">&gt;</span>
          <span>Information</span>
          <span className="checkout-page__breadcrumb-sep">&gt;</span>
          <span>Checkout</span>
        </div>

        <div className="checkout-page__layout">
          {/* Left: Forms */}
          <div className="checkout-page__main">
            {/* Contact Details */}
            <section className="checkout-page__section">
              <h2>Contact</h2>
              <ContactDetailsElement
                options={{ fields: { email: 'always' }, links: { enabled: false } } as any}
                onChange={(event) => {
                  if (event.complete && event.value?.email) {
                    setCustomerEmail(event.value.email);
                  }
                }}
              />
            </section>

            {/* Shipping Address */}
            <section className="checkout-page__section">
              <h2>Shipping Address</h2>
              <AddressElement
                options={{
                  mode: 'shipping',
                  allowedCountries: ['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE'],
                  blockPoBox: false,
                }}
                onChange={handleAddressChange}
              />
            </section>

            {/* Shipping Method */}
            <section className="checkout-page__section">
              <h2>Shipping Method</h2>

              <div className="checkout-page__shipping-option">
                <input
                  type="radio"
                  id="free-shipping"
                  name="shipping"
                  value="free"
                  checked={selectedShipping === 'free'}
                  onChange={() => {
                    setSelectedShipping('free');
                    setShippingCost(0);
                  }}
                  disabled={!isAustin}
                />
                <label
                  htmlFor="free-shipping"
                  className={!isAustin ? 'checkout-page__shipping-label--disabled' : ''}
                >
                  <span>Free Shipping (Austin, TX only)</span>
                  <span className="checkout-page__shipping-price">FREE</span>
                </label>
              </div>

              <div className="checkout-page__shipping-option">
                <input
                  type="radio"
                  id="standard-shipping"
                  name="shipping"
                  value="standard"
                  checked={selectedShipping === 'standard'}
                  onChange={() => {
                    setSelectedShipping('standard');
                    setShippingCost(499);
                  }}
                />
                <label htmlFor="standard-shipping">
                  <span>Standard Shipping (3-5 business days)</span>
                  <span className="checkout-page__shipping-price">$4.99</span>
                </label>
              </div>

              <p className="checkout-page__shipping-note">
                Orders are processed in 1-2 business days.
              </p>
            </section>

            {/* Payment */}
            <section className="checkout-page__section">
              <h2>Payment</h2>
              <p className="checkout-page__payment-note">
                All transactions are secure and encrypted.
              </p>
              <PaymentElement
                options={{
                  layout: 'tabs',
                  wallets: { applePay: 'auto', googlePay: 'auto' },
                }}
              />
            </section>

            {/* Error */}
            {error && (
              <div className="checkout-page__error" role="alert">
                <strong>⚠️ {error}</strong>
              </div>
            )}

            {/* Trust Signals */}
            <div className="checkout-page__trust">
              <div className="checkout-page__trust-row">
                <span className="checkout-page__trust-icon">🔒</span>
                <span>Secure checkout powered by <strong>Stripe</strong></span>
              </div>
              <div className="checkout-page__trust-row">
                <span className="checkout-page__trust-icon">✓</span>
                <span>256-bit SSL encryption</span>
              </div>
              <div className="checkout-page__trust-row">
                <span className="checkout-page__trust-icon">✓</span>
                <span>We never store your card details</span>
              </div>
            </div>

            {/* Purchase Button */}
            <button
              className="checkout-page__purchase-btn"
              disabled={isProcessing}
              aria-busy={isProcessing}
              type="button"
              onClick={handlePurchase}
            >
              {isProcessing ? 'Processing Payment...' : 'Purchase Now'}
            </button>

            <div className="checkout-page__trust-footer">
              <p className="checkout-page__trust-badges">
                <span>🛡️ Buyer Protection</span>
                <span>·</span>
                <span>Handmade in Texas</span>
                <span>·</span>
                <span>Small Batch</span>
              </p>
            </div>

            <p className="checkout-page__legal">
              By placing your order, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>

          {/* Right: Order Summary */}
          <aside className="checkout-page__sidebar">
            <div className="checkout-page__summary">
              {items[0]?.image && (
                <img
                  src={items[0].image}
                  alt="Order items"
                  className="checkout-page__summary-image"
                />
              )}

              <div className="checkout-page__items">
                <h3>Your Order ({items.length})</h3>
                {items.map((item) => (
                  <div key={item.id} className="checkout-page__item">
                    <div>
                      <p className="checkout-page__item-title">{item.title}</p>
                      <p className="checkout-page__item-variant">{item.variantLabel}</p>
                    </div>
                    <p className="checkout-page__item-price">{item.priceDisplay}</p>
                  </div>
                ))}
              </div>

              <div className="checkout-page__totals">
                <div className="checkout-page__total-row">
                  <span>Subtotal</span>
                  <span>{subtotalDisplay}</span>
                </div>
                {discountPercent > 0 && overrideTotal === null && (
                  <div className="checkout-page__total-row">
                    <span>Discount ({discountPercent}%)</span>
                    <span>-${(Math.round(subtotalCents * discountPercent / 100) / 100).toFixed(2)}</span>
                  </div>
                )}
                {overrideTotal !== null && (
                  <div className="checkout-page__total-row">
                    <span>Coupon applied</span>
                    <span>Adjusted</span>
                  </div>
                )}
                <div className="checkout-page__total-row">
                  <span>Shipping</span>
                  <span>{shippingCost === 0 ? 'FREE' : `$${(shippingCost / 100).toFixed(2)}`}</span>
                </div>
                <div className="checkout-page__total-row checkout-page__total-row--grand">
                  <span>Total</span>
                  <span>${(((overrideTotal !== null ? overrideTotal : Math.round(subtotalCents * (1 - discountPercent / 100))) + shippingCost) / 100).toFixed(2)}</span>
                </div>
              </div>

              <div className="checkout-page__coupon">
                <label className="checkout-page__coupon-label" htmlFor="coupon-code">Discount Code</label>
                <div className="checkout-page__coupon-row">
                  <input
                    id="coupon-code"
                    type="text"
                    className="checkout-page__input"
                    placeholder="Enter code"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value); setCouponStatus('idle'); setCouponMessage(''); }}
                    aria-label="Discount code"
                  />
                  <button
                    type="button"
                    className="checkout-page__coupon-btn"
                    onClick={handleApplyCoupon}
                    disabled={couponStatus === 'checking' || !couponCode.trim()}
                  >
                    {couponStatus === 'checking' ? '...' : 'Apply'}
                  </button>
                </div>
                {couponMessage && (
                  <p className={`checkout-page__coupon-msg ${couponStatus === 'valid' ? 'checkout-page__coupon-msg--valid' : 'checkout-page__coupon-msg--invalid'}`}>
                    {couponMessage}
                  </p>
                )}
              </div>

              <div className="checkout-page__badge">
                <span>✓</span>
                <span>Small Batch</span>
                <p>Made with intention</p>
              </div>
              <div className="checkout-page__badge">
                <span>✓</span>
                <span>Handmade in Texas</span>
                <p>By an artist</p>
              </div>
              <div className="checkout-page__badge">
                <span>✓</span>
                <span>Clean Ingredients</span>
                <p>Thoughtfully sourced</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

/**
 * CheckoutPage wraps the form in a single Elements provider with mode-based config.
 * Both AddressElement and PaymentElement render immediately on page load.
 */
const CheckoutPage: React.FC = () => {
  const { items } = useCart();
  const navigate = useNavigate();
  const [retryKey, setRetryKey] = useState(0);

  if (items.length === 0) {
    navigate('/');
    return null;
  }

  return (
    <Elements key={retryKey} stripe={stripePromise} options={elementsOptions}>
      <CheckoutForm onPaymentFailed={() => setRetryKey((k) => k + 1)} />
    </Elements>
  );
};

export default CheckoutPage;
